import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { getAllLanguage } from '../../../utils/services/languageApi';
import {
  templatesByGroup,
  translateComplexDocument,
  deleteComplexDocument,
  translateStandaloneFlow,
  getFlowByGroup,
  deleteTranslatedFlow,
  translateStandaloneBlock,
  getBlockByGroup,
  deleteTranslatedBlock,
  changeLanguage,
} from '../../../utils/services/complexDocumentsAPI';
import { Button, Icon, Tooltip, message, Dropdown, Menu } from 'antd';
import { TickIcon, ChangeIcon } from '../../../images/svgIcons';
import { LoaderContainer, HeaderText, ListContainer } from '../../SidebarMenu/styles';
import Loader from '../../../components/Loader';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { withTranslation } from 'react-i18next';
import { TEMPLATE_TRANSLATION as trans } from './translationValues';
import { useArtboardContext, CONTEXT_KEYS } from '../../../context/complexArtboardContext';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../../context/complexDocumentContext';
import { useSessionContext } from '../../../context/sessionContext';
import { ENTERPRISE_ENV, checkUserAccessRoles } from '../../../utils/helper';

const TranslateTab = props => {
  const { t } = props;
  const [enabled, setEnabled] = useState([]);
  const [translated, setTranslated] = useState([]);
  const [isLanguageDetected, setIsLanguageDetected] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isTranslating, setIsTranslating] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [languageToDelete, setLanguageToDelete] = useState(null);

  const organisationSelector = useCallback(store => store.organisationDetails, []);
  const [organisationDetails] = useSessionContext(organisationSelector);

  const  documentIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [documentId] = useDocumentContext(documentIdSelector, `translateTab`);

  const  groupIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_ID], []);
  const [groupId] = useDocumentContext(groupIdSelector, `translateTab`);

  const  templateLanguageSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.LANGUAGE], []);
  const [templateLanguage] = useDocumentContext(templateLanguageSelector, `translateTab`);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign, updateArtboard] = useArtboardContext(getCurrentDesign);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  let history = useHistory();

  const IS_REUSABLE_CONTENT_MANAGER = checkUserAccessRoles(userDetails, ['REUSABLECONTENTMANAGER']);

  useEffect(() => {
    // Fetch enabled and translated languages
    fetchData();
  }, [detectedLanguage]);

  useEffect(() => {
    setDetectedLanguage(templateLanguage);
  }, [templateLanguage]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const [enabledLanguages, translatedTemplates] = await Promise.all([
      getAllLanguage(),
      CurrentDesign === "document" ? templatesByGroup(groupId) : CurrentDesign === "flow" ? getFlowByGroup(groupId) : getBlockByGroup(groupId)
    ]);

    if (enabledLanguages.statusCode !== 109) {
        message.error(
          t(
            trans.API_ERROR_FETCH_LANGUAGES_MESSAGE.key,
            trans.API_ERROR_FETCH_LANGUAGES_MESSAGE.value,
          ),
        );
    }

    const statusCode  = [707,2605,2505]
    if (statusCode.includes(translatedTemplates?.statusCode)) {
      // Check if the 'language' field is present or if the template has a detected language
      if (translatedTemplates.data.some(item => item.hasOwnProperty('language'))) {
        setIsLanguageDetected(true);
        // Retrieve the template's detected language if it is not available
        if(CurrentDesign === "document" || CurrentDesign === "flow"){
          setDetectedLanguage(
            translatedTemplates.data.find(i => i.id === documentId)?.language,
          );
        }else{
          setDetectedLanguage(
            translatedTemplates.data.find(i => i.parentBlockId === documentId)?.language,
          );
        }
     
      } else {
        setIsLanguageDetected(false);
      }
    } else {
        message.error(
          t(
            trans.API_ERROR_FETCH_TRANSLATED_TEMPLATES_MESSAGE.key,
            trans.API_ERROR_FETCH_TRANSLATED_TEMPLATES_MESSAGE.value,
          ),
        );
    }

    // After retrieving all the translated templates, obtain the full name of the language from the enabled languages API and store it in the state
    const translated1 = translatedTemplates.data.map(i => {
      const languageCode = enabledLanguages.data.find(j => j.code === i.language);
      if (languageCode) {
        return {
          ...i,
          languageName: languageCode.name,
        };
      }
      return i;
    });
    setTranslated(translated1);

    // Remove languages from the enabled languages state if they are already present in the translated templates API
    const notTranslated = enabledLanguages.data.filter(i => {
      return !translatedTemplates.data.some(j => j.language === i.code);
    });
    setEnabled(notTranslated);
    setIsLoading(false);
  }, [documentId]);

  const handleTranslation = useCallback(async data => {
    setIsTranslating(prev => ({
      ...prev,
      [data.code]: true,
    }));
   
    let response;
    if (CurrentDesign === 'document') {
      response = await translateComplexDocument({ documentId, languageCode: data.code });
    } else if (CurrentDesign === 'flow') {
      response = await translateStandaloneFlow({ documentId, languageCode: data.code });
    }else if(CurrentDesign === 'block'){
      response = await translateStandaloneBlock({ documentId, languageCode: data.code });
    }
   
    const statusCode  = [725,2611,2501]
    if (statusCode.includes(response?.statusCode)) {
      if(ENTERPRISE_ENV && organisationDetails ? organisationDetails.languageTranslation : true) {
      message.success(
        t(
          trans.TEMPLATE_TRANSLATED_SUCCESS_MESSAGE.key,
          trans.TEMPLATE_TRANSLATED_SUCCESS_MESSAGE.value,
        ),
      );
      } else {
      history.push(`${response?.data?.id}`);
      message.success(
        t(
          trans.TEMPLATE_CREATED_SUCCESS_MESSAGE.key,
          trans.TEMPLATE_CREATED_SUCCESS_MESSAGE.value,
        ),
      );
      }  

    const dynamic_id= CurrentDesign === 'block' ? "parentBlockId" : "id"
      const newLanguage = {
        [dynamic_id]: response.data.id ,
        documentName: response.data.documentName,
        language: data.code,
        languageName: data.name,
      };

      // Include the recently translated language in the translated template state
      setTranslated(prev => [...prev, newLanguage]);

      // Exclude the translated language from the enabled languages state
      setEnabled(prev => prev.filter(i => i.code !== data.code));
    } else if (response?.errors?.some(error => error?.code === 1702)) {
      message.warn(t(trans.TRANSLATION_LIMIT_EXCEEDED_WARNING.key, trans.TRANSLATION_LIMIT_EXCEEDED_WARNING.value));
      const { planName, languageCharacter } = userDetails.planMapping;
      updateArtboard({
        [CONTEXT_KEYS.UPGRADE_MODAL_INFO]: {
          visible: true,
          message:
            planName === 'Premium'
              ? t(trans.PREMIUM_PLAN_LIMIT_EXCEEDED_ERROR.key, trans.PREMIUM_PLAN_LIMIT_EXCEEDED_ERROR.value, { emailId: 'support@perfectdoc.studio' })
              : planName === 'Free'
              ? t(trans.FREE_PLAN_UPGRADE_MESSAGE.key, trans.FREE_PLAN_UPGRADE_MESSAGE.value)
              : t(trans.STANDARD_PLAN_UPGRADE_MESSAGE.key, trans.STANDARD_PLAN_UPGRADE_MESSAGE.value, { languageCharacter }),
        },
      });
    } else if(response?.errors?.some(error => error?.code === 2422)) {
      message.error(t(trans.DOCUMENT_CREATION_LIMIT_EXCEEDED_ERROR.key, trans.DOCUMENT_CREATION_LIMIT_EXCEEDED_ERROR.value));
    } else {
        message.error(
          t(
            trans.TEMPLATE_TRANSLATE_ERROR_MESSAGE.key,
            trans.TEMPLATE_TRANSLATE_ERROR_MESSAGE.value,
          ),
        );
    }
    setIsTranslating(prev => ({
      ...prev,
      [data.code]: false,
    }));
  }, [documentId]);

  const onDelete = template => {
    // Save the template in the state for deletion after confirmation
        setLanguageToDelete(template);
    setModalVisible(true);
  };

  const handleDeletion = useCallback(async () => {
    setIsDeleting(true);

    let response;
    if (CurrentDesign === 'document') {
      response = await deleteComplexDocument(languageToDelete.id);
    } else if (CurrentDesign === 'flow') {
      response = await deleteTranslatedFlow(languageToDelete.id);
    }
    else if (CurrentDesign === 'block') {
      response = await deleteTranslatedBlock(languageToDelete.parentBlockId);
    }
  
    const statusCode  = [705,2606,2506]
    if (statusCode.includes(response?.statusCode)) {
      message.success(
        t(trans.TEMPLATE_DELETED_SUCCESS_MESSAGE.key, trans.TEMPLATE_DELETED_SUCCESS_MESSAGE.value),
      );
      // Exclude the deleted language from the translated template state

      if(CurrentDesign === "document" || CurrentDesign === "flow"){
        setTranslated(prev => prev.filter(i => i.id !== languageToDelete.id));
      }else{
        setTranslated(prev => prev.filter(i => i.parentBlockId !== languageToDelete.parentBlockId));
      }     

      // Include the deleted language in the enabled language state
      const deletedLanguage = {
        name: languageToDelete.languageName,
        code: languageToDelete.language,
      };
      setEnabled(prev => [...prev, deletedLanguage]);
      setModalVisible(false);
    } else {
        message.error(
          t(trans.TEMPLATE_DELETE_ERROR_MESSAGE.key, trans.TEMPLATE_DELETE_ERROR_MESSAGE.value),
        );
    }
    setIsDeleting(false);
  }, [languageToDelete]);

  const replaceTranslation = async (data) => {
    const removeLanguage = translated.find((i => i.language === detectedLanguage));
    const payload = {
      "id": documentId,
      "language": {
        "code": data.code,
        "name": data.name,
      },
      "type": CurrentDesign
    }
    const response = await changeLanguage(payload);

    if (response.statusCode === 1930) {
      setDetectedLanguage(data.code);

      const cloneTranslateData = JSON.parse(JSON.stringify(translated))
      const updateLanguage = cloneTranslateData.map((list) => {     
        if (list.language === detectedLanguage) {
          list.language = data.code;
          list.languageName = data.name;

          return list;
        }
        return list;
      })

      setTranslated([...updateLanguage]);

      const availableLanguage = enabled.filter((i => i.code !== data.code));

      const deletedLanguage = {
        name: removeLanguage.languageName,
        code: removeLanguage.language,
      };
      setEnabled([...availableLanguage, deletedLanguage]);
      message.success(t(trans.LANGUAGES_UPDATE_SUCCESS_LABEL.key, trans.LANGUAGES_UPDATE_SUCCESS_LABEL.value));

    } else {
      message.error(t(trans.UPDATE_LANGUAGE_ERROR_MESSAGE.key, trans.UPDATE_LANGUAGE_ERROR_MESSAGE.value))
    }
  };

  const menu = (
    <Menu>
      {enabled.map((list) => (
        <Menu.Item>
          <span  onClick={() => replaceTranslation(list)}>{list.name}</span>
        </Menu.Item>
      ))}
    </Menu>
  );

  return isLoading ? (
    <LoaderContainer>
      <Loader />
    </LoaderContainer>
  ) : isLanguageDetected ? (
    <div style={{ color: '#fff' }}>
      <HeaderText>
        {t(trans.TEMPLATE_AVAILABLE_IN_LABEL.key, trans.TEMPLATE_AVAILABLE_IN_LABEL.value)}
      </HeaderText>
      <ListContainer>
        <ul>
          {translated.map(template => {
            return (
              <li
                key={template?.id || template?.parentBlockId}
                value={template.language}
                onClick={() => history.push(`${template?.id || template?.parentBlockId}`)}
              >
                <span>{template.languageName}</span>
                <span
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  {detectedLanguage === template.language ? (
                    <div style={{ display: 'flex', alignItems: 'center',justifyContent:"center",columnGap:"10px" }}> 
                      <TickIcon />
                      {enabled.length > 0 &&
                        <Dropdown
                          overlay={menu}
                          trigger={['click']}
                          overlayClassName="complex-channels-tab-dropdown"
                          onVisibleChange={() => {
                            // Issue with first scroll within dropdown - Effect to remove the glitch
                            setTimeout(() => {
                              const dropdownMenu = document.querySelector('.ant-dropdown');
                              dropdownMenu.scrollTo(0, 1);
                            }, 0);
                          }}
                          disabled={!IS_REUSABLE_CONTENT_MANAGER}
                        >
                          <Icon component={ChangeIcon} className='changeIcon' />
                        </Dropdown>}
                     </div>                  
                  ) : (
                    <Tooltip title={t(trans.DELETE_TOOLTIP.key, trans.DELETE_TOOLTIP.value)}>
                      <Icon
                        onClick={() => IS_REUSABLE_CONTENT_MANAGER && onDelete(template)}
                        type="delete"
                        style={{ fontSize: '20px' }}
                      />
                    </Tooltip>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </ListContainer>
      {enabled.length > 0 && (
        <HeaderText>
          {t(trans.LANGUAGES_AVAILABLE_LABEL.key, trans.LANGUAGES_AVAILABLE_LABEL.value)}
        </HeaderText>
      )}
      <ListContainer noHover={true}>
        <ul>
          {enabled.map(data => (
            <li key={data.code} value={data.code}>
              <span>{data.name}</span>
              {(ENTERPRISE_ENV && organisationDetails ? organisationDetails.languageTranslation : true) ? <Button
                icon="plus"
                disabled={!IS_REUSABLE_CONTENT_MANAGER}
                loading={isTranslating[data.code]}
                onClick={() => handleTranslation(data)}
              >
                {t(trans.TRANSLATE_BUTTON.key, trans.TRANSLATE_BUTTON.value)}
              </Button> : isTranslating[data.code] ? <Icon type="loading" /> : <Icon type="plus" onClick={() => IS_REUSABLE_CONTENT_MANAGER && handleTranslation(data)} />}
            </li>
          ))}
        </ul>
      </ListContainer>
      <ConfirmationModal
        visibility={modalVisible}
        isLoading={isDeleting}
        onCancel={() => setModalVisible(false)}
        onConfirm={() => handleDeletion()}
        confirmationText={t(
          trans.TEMPLATE_DELETE_CONFIRM_MESSAGE.key,
          trans.TEMPLATE_DELETE_CONFIRM_MESSAGE.value,
        )}
        confirmButtonText={t(trans.DELETE_BUTTON.key, trans.DELETE_BUTTON.value)}
      />
    </div>
  ) : (
    <ListContainer>
      <li
        style={{ paddingLeft: 40, justifyContent: 'space-between', marginTop: '10px' }}
        className={'enableHover'}
      >
        <span style={{ fontSize: 12, textTransform: 'uppercase', color: 'white' }}>
          {t(trans.YET_TO_DETECT_LABEL.key, trans.YET_TO_DETECT_LABEL.value)}
        </span>
        <span
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <Tooltip
            title={t(trans.ASSIGN_LANGUAGE_TOOLTIP.key, trans.ASSIGN_LANGUAGE_TOOLTIP.value)}
          >
            <Dropdown
              overlay={menu}
              trigger={['click']}
              overlayClassName="complex-channels-tab-dropdown"
              onVisibleChange={() => {
                // Issue with first scroll within dropdown - Effect to remove the glitch
                setTimeout(() => {
                  const dropdownMenu = document.querySelector('.ant-dropdown');
                  dropdownMenu.scrollTo(0, 1);
                }, 0);
              }}
              disabled={!IS_REUSABLE_CONTENT_MANAGER}
            >
              <Icon component={ChangeIcon} className="changeIcon" />
            </Dropdown>
          </Tooltip>
        </span>
      </li>
    </ListContainer>
  );
};

export default withTranslation(['TEMPLATE_TRANSLATION'])(TranslateTab);
